import React from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import SectionHeader from '../components/SectionHeader';
import { Truck, Hammer, Anchor, Settings, CheckCircle2 } from 'lucide-react';

const EquipmentCategory = ({ title, items, icon: Icon }) => (
  <div className="bg-card border border-white/5 rounded-xl overflow-hidden hover:border-primary/30 transition-all duration-300 group" data-aos="fade-up">
    <div className="p-6 bg-white/5 border-b border-white/5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-background transition-colors">
        <Icon size={24} />
      </div>
      <h3 className="text-xl font-serif font-bold text-foreground">{title}</h3>
    </div>
    <div className="p-6">
      <ul className="space-y-4">
        {items.map((item, index) => (
          <li key={index} className="flex justify-between items-center border-b border-white/5 pb-2 last:border-0">
            <span className="text-muted-foreground">{item.name}</span>
            <span className="font-bold text-primary">{item.count}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const PlantEquipment = () => {
  const equipmentData = [
    {
      title: "Ground Engineering",
      icon: Anchor,
      items: [
        { name: "Mat Piling Rigs", count: "15" },
        { name: "DTH Piling Rigs", count: "15" },
        { name: "Hydraulic Rock Cutters", count: "10" },
        { name: "Pneumatic Drilling Units", count: "10" },
      ]
    },
    {
      title: "Concrete & Handling",
      icon: Truck,
      items: [
        { name: "Transit Mixers", count: "20" },
        { name: "Tower Cranes", count: "15" },
        { name: "Builder Hoists", count: "20" },
        { name: "Material Lifters", count: "20" },
      ]
    },
    {
      title: "Earthmoving",
      icon: Hammer,
      items: [
        { name: "JCB Excavators", count: "10" },
        { name: "Poclain Excavators", count: "6" },
        { name: "Dumpers / Tippers", count: "45" },
      ]
    },
    {
      title: "Finishing & Fabrication",
      icon: Settings,
      items: [
        { name: "Suspended Platforms", count: "15" },
        { name: "Concrete Power Trowels", count: "30" },
        { name: "Bar Cutting Machines", count: "25" },
        { name: "Welding Rectifiers", count: "30" },
      ]
    }
  ];

  return (
    <Layout>
      <PageHeader 
        title="Plant & Equipment" 
        subtitle="In-House Fleet for Reliable Execution"
        image="https://images.unsplash.com/photo-1580901369227-308f6f40bdeb?q=80&w=2070&auto=format&fit=crop"
      />

      {/* Intro */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <SectionHeader title="Our Machinery" subtitle="Owned & Operated" />
          <p className="text-xl text-muted-foreground leading-relaxed" data-aos="fade-up">
            Unlike many competitors who rely on rentals, TOTO INFRA owns and operates a massive fleet of specialized construction equipment. This ensures we have full control over project timelines, maintenance, and availability.
          </p>
        </div>
      </section>

      {/* Equipment Grid */}
      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {equipmentData.map((category, index) => (
              <EquipmentCategory key={index} {...category} />
            ))}
          </div>
        </div>
      </section>

      {/* Client Benefits */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div data-aos="fade-right">
              <SectionHeader title="The TOTO Advantage" subtitle="Why It Matters" align="left" className="mb-8" />
              <div className="space-y-6">
                {[
                  { title: "Faster Mobilization", desc: "We can deploy machinery to sites immediately without waiting for third-party vendors." },
                  { title: "Reliability", desc: "Regular in-house maintenance ensures minimal breakdown downtime." },
                  { title: "Cost Efficiency", desc: "Owning equipment reduces overheads, passing savings to our clients." },
                  { title: "Quality Control", desc: "Our operators are trained specifically for our high standards of precision." }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <CheckCircle2 className="text-primary shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="text-lg font-bold text-foreground">{item.title}</h4>
                      <p className="text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div data-aos="fade-left" className="relative">
              <img 
                src="https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?q=80&w=2070&auto=format&fit=crop" 
                alt="Construction Site" 
                className="rounded-xl shadow-2xl border border-white/10"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary p-6 rounded-xl shadow-lg">
                <p className="text-background font-bold text-3xl">200+</p>
                <p className="text-background/80 font-medium">Total Assets</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PlantEquipment;
